<template>
  <div class="mx-auto flex w-full flex-col justify-center md:max-w-screen-xl">
    <section
      class="my-4 hidden grid-cols-2 place-items-center gap-6 px-4 md:grid md:grid-cols-6 lg:my-10"
      :class="isGuestMode ? 'order-3' : ''"
    >
      <nuxt-link v-for="cat in categories" :key="cat.to" :to="cat.to">
        <ProductCategoryCard
          :title="cat.title"
          class="w-[150px] md:w-full md:max-w-[250px]"
        >
          <template #image>
            <img :src="cat.path" :alt="cat.alt" />
          </template>
        </ProductCategoryCard>
      </nuxt-link>
    </section>

    <div class="block w-full space-y-4 py-10 md:hidden">
      <section
        class="categories-section grid w-full grid-flow-col gap-5 overflow-x-auto px-4"
      >
        <nuxt-link v-for="cat in categories" :key="cat.to" :to="cat.to">
          <ProductCategoryCard
            :title="cat.title"
            class="w-[150px] md:w-full md:max-w-[250px]"
          >
            <template #image>
              <img :src="cat.path" :alt="cat.alt" />
            </template>
          </ProductCategoryCard>
        </nuxt-link>
      </section>
      <div class="flex items-center justify-end px-4">
        <div class="flex items-center gap-2">
          <div
            class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-gray-100"
            @click="scrollLeft"
            :class="{
              'pointer-events-none opacity-50': atStart,
            }"
          >
            <ZenniIconsIconChevronRight
              class="text-grays-black h-7 w-7 rotate-180"
            />
          </div>
          <div
            class="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-gray-100"
            @click="scrollRight"
            :class="{
              'pointer-events-none opacity-50': atEnd,
            }"
          >
            <ZenniIconsIconChevronRight class="text-grays-black h-7 w-7" />
          </div>
        </div>
      </div>
    </div>

    <LandingVisionHero
      v-if="!isAdminGift"
      :class="isGuestMode ? 'order-1' : ''"
    />
    <LandingHighLights
      v-if="!isAdminGift"
      :class="isGuestMode ? 'order-2' : ''"
    />
    <div class="order-4 mx-auto flex w-full flex-col gap-10 px-2 py-10">
      <LandingVisionBestSelling
        title="Bestselling Eyeglasses"
        category="bestselling"
      />
      <!-- we will use sunglasses as search query for testing in stg. Dev has dirty data and they might not show the correct images-->
      <LandingVisionBestSelling
        title="Bestselling Sunglasses"
        active-image="tinted"
        category="bestselling-sunglasses"
      />
      <div class="mt-10 max-w-7xl px-2 md:mx-auto">
        <h2 class="text-grays-darkest flex-1 text-2xl font-semibold leading-9">
          Shop by Frame Shape
        </h2>
        <div
          class="my-4 grid grid-cols-2 place-items-center gap-4 md:flex md:flex-wrap md:justify-center lg:my-10"
        >
          <ZnFrameStyleCard
            v-for="(style, index) in frameStyleCards.slice(0, 6)"
            :key="index"
            v-bind="style"
          />
        </div>
      </div>
      <ZnButton class="border-teal-dark mx-auto" type="secondary">
        <nuxt-link
          to="/products/list?Frame___Gender_search=[women_s,Women_s,Unisex,unisex]&Frame___Gender_search=[men_s,Men_s,Unisex,unisex]"
          >Shop all</nuxt-link
        >
      </ZnButton>
    </div>
  </div>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import {
  useEmployeeAlgolia,
  useNuxtApp,
  ref,
  useImage,
  onMounted,
  onUnmounted,
} from '#imports'
import { useGuestStore } from '~/store/guest'
import type { ZnAlgoliaProduct } from '@/types/algolia-results/algolia-types'
import {
  MEN_GENDER_GROUP_STRING,
  WOMEN_GENDER_GROUP_STRING,
} from '~/data/categories'
import { CAT_EYE_GROUP, WRAP_AROUND_GROUP } from '~/data/filter-groups'

const { isGuestMode, isAdminGift } = storeToRefs(useGuestStore())
const { $shapeConfig } = useNuxtApp()
const img = useImage()
const shapeCategoryDefaultFilters: Record<string, string[]> = {
  cat_eye: [WOMEN_GENDER_GROUP_STRING],
  Cat__Eye: [WOMEN_GENDER_GROUP_STRING],
  Cat___Eye: [WOMEN_GENDER_GROUP_STRING],
}
const categories = [
  {
    to: '/products/list/women',
    path: '/img/landing/vision/category-women.png',
    title: "Women's Eyeglasses",
    alt: 'Woman wearing eyeglasses and a stethoscope.',
  },
  {
    to: '/products/list/men',
    path: '/img/landing/vision/category-men.png',
    title: "Men's Eyeglasses",
    alt: 'Man wearing eyeglasses and a hard hat.',
  },
  {
    to: '/products/list/kids',
    path: '/img/landing/vision/category-kids.png',
    title: "Kids' Eyeglasses",
    alt: 'Kids wearing eyeglasses.',
  },
  {
    to: '/products/list/sunglasses?activeImage=tinted',
    path: '/img/landing/vision/category-sunglasses.png',
    title: 'Sunglasses',
    alt: 'Woman wearing sunglasses.',
  },
  {
    to: '/products/list/sports',
    path: '/img/landing/vision/category-sports.png',
    title: 'Sports',
    alt: 'Woman wearing sports glasses.',
  },
  {
    to: '/products/list/premium',
    path: '/img/landing/vision/category-premium.png',
    title: 'Premium',
    alt: 'Woman wearing premium glasses.',
  },
]

const shapeProductsSearchParams = {
  queryParams: {
    query: '',
    requestOptions: {
      filters: 'objectType:"product-variant"',
      hitsPerPage: 10,
      facets: ['*'], // get all facets for filtering available frame styles in shop by frame style slider
      attributesToRetrieve: [], // no need to retrieve attributes for this search
    },
  },
}

const frameStyleCards = ref<any[]>([])
const { search, result: data } = useEmployeeAlgolia<ZnAlgoliaProduct>()
await search(shapeProductsSearchParams)
const availableFrameShapes = Object.keys(
  data.value?.facets?.Frame___Shape_search || {},
)
$shapeConfig.forEach((shape) => {
  if (availableFrameShapes.includes(shape.code)) {
    // we need to verify if the code belongs to a filter group
    const filterGroup = [WRAP_AROUND_GROUP, CAT_EYE_GROUP].find((group) =>
      group.filter.includes(shape.code),
    )
    const categoryUrl = `/products/list/?Frame___Shape_search=${
      filterGroup?.filter || shape.code
    }&Frame___Gender_search=${(
      shapeCategoryDefaultFilters[shape.code] ?? [
        WOMEN_GENDER_GROUP_STRING,
        MEN_GENDER_GROUP_STRING,
      ]
    ).join('&Frame___Gender_search=')}`
    if (
      filterGroup &&
      frameStyleCards.value.find((s) => s.id === filterGroup.imageFrom)
    ) {
      return
    }
    frameStyleCards.value.push({
      id: filterGroup?.imageFrom || shape.code,
      title: shape.values.label?.data || '',
      image: img(shape.values.imageurl?.data || '', {
        width: 115,
        height: 41,
      }),
      alt: shape.values.label?.data || '',
      href: categoryUrl,
    })
  }
})
const atStart = ref(true)
const atEnd = ref(false)
const updateScrollPosition = () => {
  const container = document.querySelector(`.categories-section`)
  if (container) {
    atStart.value = container.scrollLeft === 0
    atEnd.value =
      Math.abs(
        container.scrollLeft - (container.scrollWidth - container.clientWidth),
      ) < 2 // allow for rounding
  }
}

const scrollLeft = () => {
  const container = document.querySelector(`.categories-section`)

  if (container) {
    container.scrollBy({
      left: -300,
      behavior: 'smooth',
    })
  }
}

const scrollRight = () => {
  const container = document.querySelector(`.categories-section`)
  if (container) {
    container.scrollBy({
      left: 300,
      behavior: 'smooth',
    })
  }
}

onMounted(() => {
  const container = document.querySelector(`.categories-section`)
  if (container) {
    container.addEventListener('scroll', updateScrollPosition)
    updateScrollPosition()
  }
})

onUnmounted(() => {
  const container = document.querySelector(`.categories-section`)
  if (container) {
    container.removeEventListener('scroll', updateScrollPosition)
  }
})
</script>
